import React, { useCallback, useEffect, useRef, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import {
  SkeletonAvatar,
  SkeletonLine,
  SkeletonText,
} from "@ui-components/Skeleton/GenericSkeleton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { Attachment, TicketDetail } from "../TicketDetail";
import { AttachmentList } from "./AttachmentUI";
import TicketParticipants from "./TicketParticipants";
import { DetailCard, DetailField } from "./ticketDetails/DetailField";
import { InitialsAvatar } from "./ticketDetails/InitialsAvatar";
import { TicketConversation } from "./ticketDetails/TicketConversation";
import {
  TicketDetailsHeader,
  toServiceIds,
} from "./ticketDetails/TicketDetailsHeader";
import { TicketReplyBox } from "./ticketDetails/TicketReplyBox";
import { JiraText } from "./ticketDetails/JiraText";
import { isClosedStatus } from "../SupportFields";
import { useWindowSize } from "@shared/hooks/useWindowSize";

type Props = {
  isOpen: boolean;
  ticketId: string | number | null;
  /**
   * What the ticket list already knows about the row. The detail endpoints
   * return neither the ticket number (KS-484) nor the request type the list
   * shows, so the header takes them from here.
   */
  summary?: { number?: string | number; type?: string };
  fetchTicketById: (id: string | number) => Promise<TicketDetail>;
  onClose: () => void;
  onDownloadAttachment?: (
    ticketId: string | number,
    att: Attachment
  ) => Promise<void>;
  onSendReply?: (
    ticketId: string | number,
    message: string,
    files?: File[]
  ) => Promise<void>;
  isKontrolEdge?: boolean;
};

function TicketDetailsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        <SkeletonText lines={3} widths="random" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <SkeletonAvatar size={28} />
            <div className="flex-1">
              <SkeletonLine w="180px" h={14} />
              <SkeletonText lines={2} randomRange={[60, 95]} />
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <SkeletonLine w="60%" h={14} />
        <SkeletonLine w="80%" h={14} />
        <SkeletonLine w="50%" h={14} />
      </div>
    </div>
  );
}

/**
 * Detail of a support ticket (CTX-1121): a header with number, type, subject,
 * status and priority; the description and the conversation with its reply
 * box on the left; details, people and attachments on the right.
 *
 * From `lg` up the modal has a fixed height and each column scrolls on its
 * own (CTX-1343): the left one gives the conversation all the room the
 * description leaves, so it opens on its latest message with the reply box in
 * view instead of cut halfway, and the right one scrolls without moving it.
 * On narrow screens the columns stack and the modal scrolls as a whole.
 *
 * On a phone (CTX-1433) the modal takes the screen and the reply box is its
 * footer like a chat's; the side column follows the conversation as it is. A
 * closed or canceled ticket takes no reply, so it shows none.
 */
export default function TicketDetailsModal({
  isOpen,
  ticketId,
  summary,
  fetchTicketById,
  onClose,
  onDownloadAttachment,
  onSendReply,
  isKontrolEdge = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [downloadingMap, setDownloadingMap] = useState<
    Record<string | number, boolean>
  >({});
  const [isSending, setIsSending] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const conversationListRef = useRef<HTMLDivElement>(null);

  // Fetch details whenever the modal opens with a ticketId
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isOpen || ticketId == null) return;
      setLoading(true);
      setTicket(null);
      try {
        const data = await fetchTicketById(ticketId);
        if (!cancelled) setTicket(data);
      } catch (e) {
        console.error("Ticket detail fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId, fetchTicketById]);

  // From lg up the conversation scrolls on its own and opens on the latest
  // message, so the reply box under it is always in view (CTX-1121). Stacked
  // it does not scroll, and this does nothing. Re-runs after a reply, when
  // the refetched ticket comes back with it.
  useEffect(() => {
    const list = conversationListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [ticket]);

  const handleDownload = async (att: Attachment) => {
    if (!onDownloadAttachment || ticketId == null) return;
    setDownloadingMap((prev) => ({ ...prev, [att.id]: true }));
    try {
      await onDownloadAttachment(ticketId, att);
    } finally {
      setDownloadingMap((prev) => ({ ...prev, [att.id]: false }));
    }
  };

  const handleSend = async () => {
    if (!onSendReply || ticketId == null) return;
    const message = replyText.trim();
    if (!message || isSending) return;
    try {
      setIsSending(true);
      await onSendReply(ticketId, message, replyFiles);
      // Re-fetch so the new reply shows up in the conversation.
      setTicket(await fetchTicketById(ticketId));
      setReplyText("");
      setReplyFiles([]);
      setSuccessOpen(true);
    } catch (err) {
      setErrorMsg(
        (err as Error)?.message || "Failed to send reply. Please try again."
      );
      setErrorOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleModalClose = useCallback(() => {
    if (isSending) return;
    onClose();
  }, [isSending, onClose]);

  const serviceIds = toServiceIds(ticket?.service_id);
  const ticketAttachments = ticket?.attachments ?? [];
  const isPhone = useWindowSize().width < 640;
  const isClosed = ticket != null && isClosedStatus(ticket.status);

  const replyBox = isClosed ? (
    <p className="text-xs text-semantic-text-secondary">
      This ticket is closed. Open a new one to follow up.
    </p>
  ) : (
    <TicketReplyBox
      value={replyText}
      onChange={setReplyText}
      files={replyFiles}
      onFilesChange={setReplyFiles}
      allowAttachments={isKontrolEdge}
      onSend={handleSend}
      isSending={isSending}
      canSend={Boolean(onSendReply)}
    />
  );
  const showReplyInFooter = isPhone && !loading && ticket != null;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={
        ticket ? (
          <TicketDetailsHeader
            number={summary?.number}
            type={summary?.type}
            ticket={ticket}
          />
        ) : (
          "Ticket details"
        )
      }
      customWidth="max-w-5xl"
      fullScreenOnPhone
      footer={
        showReplyInFooter ? (
          <div className="w-full pb-3">{replyBox}</div>
        ) : undefined
      }
      // Fixed at 90vh, and the body a flex column, so the height reaches the
      // columns through flex sizing; a percentage height would not resolve
      // against a wrapper that only has min/max heights.
      minHeight="90vh"
      bodyClassName="flex flex-col px-3 pt-3 pb-4 sm:px-6 sm:pt-4 sm:pb-6"
    >
      <LoadingWrapper isLoading={loading} skeleton={<TicketDetailsSkeleton />} />
      {!loading && ticket && (
        <div className="grid grid-cols-1 gap-4 text-sm lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-4 lg:min-h-0">
            {ticket.description_text && (
              <DetailCard title="Description" className="shrink-0">
                <p className="whitespace-pre-line leading-5 text-semantic-text-primary [overflow-wrap:anywhere] lg:max-h-40 lg:overflow-y-auto">
                  <JiraText text={ticket.description_text} />
                </p>
              </DetailCard>
            )}

            <DetailCard
              title="Conversation"
              className="flex flex-col lg:min-h-0 lg:flex-1"
            >
              <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
                {/* From lg up the list takes whatever the column leaves and
                    the reply box stays pinned under it. Stacked, the modal
                    is the only scroll: a capped list and a capped
                    description inside it trapped the thumb between three
                    scrolls on a phone (CTX-1339). */}
                <div
                  ref={conversationListRef}
                  className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1"
                >
                  <TicketConversation
                    conversations={ticket.conversations}
                    reporterName={
                      ticket.requester?.name || ticket.requester?.email
                    }
                    downloadingMap={downloadingMap}
                    onDownload={handleDownload}
                  />
                </div>
                {!isPhone && replyBox}
              </div>
            </DetailCard>
          </div>

          <div className="flex min-w-0 flex-col gap-4 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            <DetailCard title="Details">
              <dl className="flex flex-col gap-3">
                {/* Kontrol Edge's End Customer: whose ticket it is, which a
                  partner looking at all its customers cannot tell from the
                  service ids alone (Jose, CTX-1256). */}
                {ticket.custom_fields?.cf_end_customer && (
                  <DetailField label="Customer">
                    {String(ticket.custom_fields.cf_end_customer)}
                  </DetailField>
                )}
                {serviceIds.length > 0 && (
                  <DetailField label="Services">
                    {/* The side column has room for all of them, so no
                      "+N" popover as in the table. */}
                    <span className="flex flex-wrap gap-1">
                      {serviceIds.map((id) => (
                        <GenericTag
                          key={id}
                          label={id}
                          variant="neutral"
                          size="small"
                        />
                      ))}
                    </span>
                  </DetailField>
                )}
                {/* Kontrol Edge fills it with the type the header already shows. */}
                {ticket.custom_fields?.cf_tipo &&
                  ticket.custom_fields.cf_tipo !== summary?.type && (
                    <DetailField label="Request type">
                      {String(ticket.custom_fields.cf_tipo)}
                    </DetailField>
                  )}
                {ticket.date?.friendly && (
                  <DetailField label="Created">
                    {ticket.date.friendly}
                  </DetailField>
                )}
              </dl>
            </DetailCard>

            <DetailCard title="People">
              <dl className="flex flex-col gap-3">
                <DetailField label="Reporter">
                  <span className="flex min-w-0 items-center gap-2">
                    <InitialsAvatar
                      name={ticket.requester?.name || ticket.requester?.email}
                    />
                    <span className="min-w-0">
                      <span className="block truncate">
                        {ticket.requester?.name || "—"}
                      </span>
                      {ticket.requester?.email &&
                        ticket.requester.email !== ticket.requester.name && (
                          <span className="block truncate text-xs text-semantic-text-secondary">
                            {ticket.requester.email}
                          </span>
                        )}
                    </span>
                  </span>
                </DetailField>
                {isKontrolEdge && ticketId != null && (
                  <TicketParticipants ticketId={ticketId} />
                )}
              </dl>
            </DetailCard>

            {ticketAttachments.length > 0 && (
              <DetailCard title={`Attachments · ${ticketAttachments.length}`}>
                <AttachmentList
                  attachments={ticketAttachments}
                  downloadingMap={downloadingMap}
                  onDownload={handleDownload}
                  columns={1}
                />
              </DetailCard>
            )}
          </div>
        </div>
      )}

      <GenericAlert
        isOpen={successOpen}
        onClose={() => setSuccessOpen(false)}
        onConfirm={() => setSuccessOpen(false)}
        variant="success"
        title="Reply sent"
        description="Your message was delivered successfully."
        confirmLabel="OK"
      />

      <GenericAlert
        isOpen={errorOpen}
        onClose={() => setErrorOpen(false)}
        onConfirm={() => setErrorOpen(false)}
        variant="error"
        title="Could not send reply"
        description={errorMsg || "Something went wrong. Please try again."}
        confirmLabel="Close"
      />
    </GenericModal>
  );
}
